import { ReducerTypes, on } from '@ngrx/store';
import { ActionCreator } from '@ngrx/store/src/models';

import { createImmutableReducer } from '../../base/store/helpers';
import { BetslipGroupPick } from '../../core/picks/betslip-group-pick';
import { BetslipPick } from '../../core/picks/betslip-pick';
import { PriceType } from '../../core/picks/pick-models';
import BetslipPickActions from './actions';
import { defaultLastPickErrorState, defaultPickState, defaultPickUiState } from './state';

export const picksReducer = createImmutableReducer(
    defaultPickState,
    on(BetslipPickActions.addPick, (state, action) => {
        const pick = action.pick;
        if (state.pickList.some((p) => p.id.isEqual(pick.id))) {
            return state;
        }
        state.pickList = [...state.pickList, pick];

        return state;
    }),
    on(BetslipPickActions.removePick, (state, action) => {
        const pickId = action.pickId;
        state.pickList = state.pickList.filter((p) => !p.id.isEqual(pickId));

        return state;
    }),
    on(BetslipPickActions.removeMultiplePicks, (state, action) => {
        state.pickList = state.pickList.filter((p) => !action.pickIds.some((id) => p.id.isEqual(id)));

        return state;
    }),
    on(BetslipPickActions.updateV1Picks, BetslipPickActions.updateV2Picks, BetslipPickActions.updateGroupPicks, (state, action) => {
        state.pickList = state.pickList.map((pick) => {
            const update = (action.pickUpdates as { pick: BetslipPick }[]).find((pickUpdate) => pick.id.isEqual(pickUpdate.pick.id));

            return update ? update.pick : pick;
        });

        return state;
    }),
    on(BetslipPickActions.replacePick, (state, action) => {
        const { pickId, newPick } = action;
        state.pickList = state.pickList.map((pick) => {
            if (pick.id.isEqual(pickId)) {
                return newPick;
            }

            return pick;
        });

        return state;
    }),
    on(BetslipPickActions.setStartingPrice, (state, action) => {
        state.pickList = state.pickList.map((pick) => {
            if (pick.id.isEqual(action.pickId)) {
                const newPick = pick.copy();
                newPick.priceType = PriceType.StartingPrice;
                newPick.acceptedPrice = {
                    price: { ...newPick.currentPrice! },
                    isManuallyAccepted: true,
                };

                return newPick;
            }

            return pick;
        });

        return state;
    }),

    on(BetslipPickActions.setFixedPrice, (state, action) => {
        state.pickList = state.pickList.map((pick) => {
            if (pick.id.isEqual(action.pickId)) {
                const newPick = pick.copy();
                newPick.priceType = PriceType.Fixed;
                newPick.acceptedPrice = { price: { ...newPick.currentPrice! }, isManuallyAccepted: true };

                return newPick;
            }

            return pick;
        });

        return state;
    }),
    on(BetslipPickActions.acceptCurrentPrice, (state, action) => {
        const pickIds = action.pickIds;
        state.pickList = state.pickList.map((pick) => {
            if (pickIds.some((id) => id.isEqual(pick.id))) {
                const newPick = pick.copy();
                newPick.acceptedPrice = {
                    price: { ...newPick.currentPrice! },
                    isManuallyAccepted: action.isManuallyAccepted,
                };

                return newPick;
            }

            return pick;
        });

        return state;
    }),
    on(BetslipPickActions.expandBetBuilderLegs, (state, action) => {
        const pickId = action.pickId.toString();
        const pickUiState = state.ui[pickId] ?? { ...defaultPickUiState };
        pickUiState.expandLegs = true;
        state.ui[pickId] = pickUiState;

        return state;
    }),
    on(BetslipPickActions.collapseBetBuilderLegs, (state, action) => {
        const pickId = action.pickId.toString();
        const pickUiState = state.ui[pickId] ?? { ...defaultPickUiState };
        pickUiState.expandLegs = false;
        state.ui[pickId] = pickUiState;

        return state;
    }),
    on(BetslipPickActions.setSourceOfBet, (state, action) => {
        state.pickList = state.pickList.map((pick) => {
            if (pick.id.isEqual(action.pickId)) {
                const newPick = pick.copy();
                newPick.tracking = pick.tracking = { source: action.source, affiliate: action.affiliate };

                return newPick;
            }

            return pick;
        });

        return state;
    }),
    on(BetslipPickActions.setSourceOfBetSuffix, (state, action) => {
        state.pickList = state.pickList.map((pick) => {
            if (pick.id.isEqual(action.pickId)) {
                const newPick = pick.copy();
                newPick.tracking = { ...pick.tracking, sourceOfBetSuffix: action.suffix };

                return newPick;
            }

            return pick;
        });

        return state;
    }),
    on(BetslipPickActions.setMultipleSourceOfBetSuffix, (state, action) => {
        const pickIds = action.pickIds;
        state.pickList = state.pickList.map((pick) => {
            if (pickIds.some((id) => id.isEqual(pick.id))) {
                const newPick = pick.copy();
                if (BetslipGroupPick.isPick(newPick)) {
                    newPick.getLegs().map((leg) => (leg.tracking = { ...leg.tracking, sourceOfBetSuffix: action.suffix }));
                } else {
                    newPick.tracking = { ...pick.tracking, sourceOfBetSuffix: action.suffix };
                }

                return newPick;
            }

            return pick;
        });

        return state;
    }),
    on(BetslipPickActions.setTrackingNumpadAction, (state, action) => {
        const pickIds = action.pickIds;
        state.pickList = state.pickList.map((pick) => {
            if (pickIds.some((id) => id.toString() === pick.id.toString())) {
                const newPick = pick.copy();
                newPick.tracking = { ...pick.tracking, numpadAction: action.action };

                return newPick;
            }

            return pick;
        });

        return state;
    }),
    on(BetslipPickActions.addPicksHighlight, (state, action) => {
        action.payload.pickIds.forEach((pick) => {
            const pickId = pick.toString();
            const pickUiState = state.ui[pickId] ?? { ...defaultPickUiState };
            pickUiState.highlightPick = true;
            state.ui[pickId] = pickUiState;
        });

        return state;
    }),
    on(BetslipPickActions.removePicksHighlight, (state, action) => {
        action.pickIds.forEach((pick) => {
            const pickId = pick.toString();
            const pickUiState = state.ui[pickId] ?? { ...defaultPickUiState };
            pickUiState.highlightPick = false;
            state.ui[pickId] = pickUiState;
        });

        return state;
    }),
    on(BetslipPickActions.addLastPickError, (state, action) => {
        state.lastPickError.hasError = true;
        state.lastPickError.previousHadError = false;
        state.lastPickError.pickId = action.pickId;

        return state;
    }),
    on(BetslipPickActions.removeLastPickError, (state, _) => {
        state.lastPickError = defaultLastPickErrorState;

        return state;
    }),
    on(BetslipPickActions.lastPickErrorRemovePickId, (state, _) => {
        state.lastPickError.hasError = true;
        state.lastPickError.previousHadError = true;
        state.lastPickError.pickId = undefined;

        return state;
    }),
);

export function pickSelectionHandlers<TState extends { picks: Record<string, { isSelected: boolean; isLocked: boolean }> }>(): ReducerTypes<
    TState,
    ActionCreator[]
>[] {
    return [
        on(BetslipPickActions.removeMultiplePicks, (state, action) => {
            for (const pickId of action.pickIds) {
                delete state.picks[pickId.toString()];
            }

            return state;
        }),
        on(BetslipPickActions.togglePick, (state, action) => {
            const pickId = action.pickId.toString();
            if (!state.picks[pickId]) {
                return state;
            }
            state.picks[pickId].isSelected = !state.picks[pickId].isSelected;

            return state;
        }),
        on(BetslipPickActions.toggleAllPicks, (state, action) => {
            Object.values(state.picks).forEach((p) => {
                p.isSelected = action.select;
            });

            return state;
        }),
        on(BetslipPickActions.selectPick, (state, action) => {
            const pickId = action.pickId.toString();
            if (!state.picks[pickId]) {
                return state;
            }
            state.picks[pickId].isSelected = true;

            return state;
        }),
        on(BetslipPickActions.deselectPick, (state, action) => {
            const pickId = action.pickId.toString();
            if (!state.picks[pickId]) {
                return state;
            }
            state.picks[pickId].isSelected = false;

            return state;
        }),
        on(BetslipPickActions.changeSelection, (state, action) => {
            action.pickIds.forEach((id) => {
                const pickId = id.toString();
                if (state.picks[pickId]) {
                    state.picks[pickId].isSelected = action.selection;
                }
            });

            return state;
        }),
        on(BetslipPickActions.changeLockState, (state, action) => {
            action.pickIds.forEach((id) => {
                const pickId = id.toString();
                if (state.picks[pickId]) {
                    state.picks[pickId].isLocked = action.isLocked;
                }
            });

            return state;
        }),
        on(BetslipPickActions.requestRemoveAllPicks, (state) => {
            state.picks = {};

            return state;
        }),
    ];
}
